import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { Select } from "antd";
import toast from "react-hot-toast";
import Loader from "../Loader";
import { bulkAssignTask } from "../../services/taskServices";
import { fetchCompanyEmployees } from "../../services/EmployeeDetailServices";
import { employeeDetails } from "../../auth";
import api from "../../api/axios";
import { useTranslation } from "react-i18next";

const MultiAssignModal = ({ show, handleClose, taskDetails }) => {
  const { t } = useTranslation();
  const employeeDirectory = employeeDetails();
  const loginEmployeeId = localStorage.getItem("employeeID");
  
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState("");
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (show) {
      getCompanyEmployees(employeeDirectory?.company?.company_id);
      if (taskDetails?.task_id) {
        getExistingAssignments(taskDetails.task_id);
      }
    }
  }, [show, employeeDirectory?.company?.company_id, taskDetails?.task_id]);

  const getCompanyEmployees = async (companyId) => {
    try {
      const response = await fetchCompanyEmployees(companyId);
      if (response.length > 0) {
        const employeeOptions = response.map((employee) => ({
          value: employee.id,
          label: `${employee.first_name} ${employee.last_name}`,
        }));
        setEmployees(employeeOptions);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    }
  };

  const getExistingAssignments = async (taskId) => {
    try {
      const response = await api.get(`tasks/${taskId}/assignments`);
      if (response.data?.success && response.data?.assignments) {
        const assignedEmployeeIds = response.data.assignments.map(assignment => 
          assignment.assigned_to
        );
        setSelectedEmployees(assignedEmployeeIds);
      }
    } catch (error) {
      console.error("Error fetching existing assignments:", error);
      // Don't show error to user, just keep empty selection
    }
  };

  const handleEmployeeChange = (values) => {
    setSelectedEmployees(values);
    setError("");
  };

  const handleAssign = async () => {
    if (selectedEmployees.length === 0) {
      setError(t("Please select at least one employee"));
      return;
    }

    setLoader(true);
    setError("");

    try {
      const bulkResponse = await bulkAssignTask(
        taskDetails.task_id, 
        selectedEmployees, 
        loginEmployeeId
      );

      if (bulkResponse?.success) {
        handleClose();
        toast.success(t("Task assigned, successfully!"));
      } else {
        setError(t("Failed to assign task to employees. Please try again."));
      }
    } catch (error) {
      console.error("Error in multi-assignment:", error);
      setError(t("Something went wrong while assigning the task. Please try again."));
    } finally {
      setLoader(false);
    }
  };

  const closeModal = () => {
    // Don't reset selectedEmployees immediately to allow user to see current assignments
    setError("");
    handleClose();
  };

  // Reset form when modal is completely closed
  useEffect(() => {
    if (!show) {
      setSelectedEmployees([]);
      setError("");
    }
  }, [show]);

  return (
    <>
      <Modal
        show={show}
        onHide={closeModal}
        backdrop="static"
        keyboard={false}
        size="md"
        style={{ zIndex: 1050 }}
      >
        <Modal.Header>
          <Modal.Title>{t("Assign Task to Multiple Employees")}</Modal.Title>
          <Button 
            variant="link" 
            className="btn-close" 
            onClick={closeModal}
            style={{ border: 'none', background: 'none', fontSize: '1.5rem' }}
          >
            ×
          </Button>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          
          <Form>
            <div className="mb-3">
              <label className="form-label fw-bold">{t("Task Title")}:</label>
              <p className="text-muted">{taskDetails?.title}</p>
            </div>
            
            <div className="mb-3">
              <label className="form-label fw-bold">{t("Select Employees")}:</label>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <Select
                  mode="multiple"
                  style={{ width: "100%", minHeight: "38px" }}
                  placeholder={t("Select employees to assign this task")}
                  value={selectedEmployees}
                  onChange={handleEmployeeChange}
                  options={employees}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  allowClear
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                  dropdownStyle={{ zIndex: 9999 }}
                  popupClassName="multi-assign-dropdown"
                />
              </div>
              <small className="text-muted">
                {t("You can select multiple employees to assign this task to all of them")}
              </small>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={loader}>
            {t("Cancel")}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssign} 
            disabled={loader || selectedEmployees.length === 0}
          >
            {loader ? t("Assigning...") : t("Assign Task")}
          </Button>
        </Modal.Footer>
      </Modal>
      {loader && <Loader show={loader} />}
    </>
  );
};

export default MultiAssignModal;